import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, IconButton, Stack, Tooltip, Typography, alpha,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PremiumHeader from "./components/PremiumHeader";
import FilterPanel from "./components/FilterPanel";
import TimetableGrid from "./components/TimetableGrid";
import { reportsApi } from "../shared/api";
import { defaultFilter, FilterState, Options, StudentBlock, StudentIndexItem } from "./types";
import StepUpSpinner from "../../../Components/Loader/StepUpSpinner";

// ── Palette (matches TimetableGrid + PremiumHeader) ─────────────────
const NAVY = "#1b3a6b";
const INK = "#0f172a";
const MUTED = "#64748b";
const HAIRLINE = "#e2e8f0";
const PAGE_BG = "#f8fafc";

function toParams(f: FilterState) {
  return {
    week_start: f.week_start || undefined,
    date_from: f.date_from || undefined,
    date_to: f.date_to || undefined,
    student_ids: f.student_ids.length ? f.student_ids : undefined,
    location_ids: f.location_ids.length ? f.location_ids : undefined,
    grade_ids: f.grade_ids.length ? f.grade_ids : undefined,
    staff_ids: f.staff_ids.length ? f.staff_ids : undefined,
    subject_ids: f.subject_ids.length ? f.subject_ids : undefined,
    study_modes: f.study_modes.length ? f.study_modes : undefined,
    assignment_statuses: f.assignment_statuses.length ? f.assignment_statuses : undefined,
    include_one_off: f.include_one_off ? 1 : undefined,
    student_search: f.student_search || undefined,
  };
}

// Format an ISO week range as "Sep 21 – 27, 2026" or "Sep 28 – Oct 4, 2026".
function formatWeekRange(from?: string | null, to?: string | null) {
  if (!from || !to) return "";
  const f = new Date(from + "T00:00:00");
  const t = new Date(to + "T00:00:00");
  if (isNaN(f.getTime()) || isNaN(t.getTime())) return `${from} – ${to}`;
  const sameMonth = f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear();
  const fMonth = f.toLocaleString("en-US", { month: "short" });
  const tMonth = t.toLocaleString("en-US", { month: "short" });
  return sameMonth
    ? `${fMonth} ${f.getDate()} – ${t.getDate()}, ${t.getFullYear()}`
    : `${fMonth} ${f.getDate()} – ${tMonth} ${t.getDate()}, ${t.getFullYear()}`;
}

// ── Small stat cell for the summary strip ────────────────────────────
const StatCell: React.FC<{ label: string; value: React.ReactNode; icon: React.ReactNode; accent?: string }> = ({
  label, value, icon, accent,
}) => (
  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ px: 1.5, py: 1, flex: "1 1 140px", minWidth: 120 }}>
    <Box
      sx={{
        width: 32, height: 32, borderRadius: "10px",
        bgcolor: alpha(accent || NAVY, 0.1),
        color: accent || NAVY,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: INK, lineHeight: 1.1 }}>
        {value ?? 0}
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </Typography>
    </Box>
  </Stack>
);

export default function StudentTimetableReportPage() {
  const [filter, setFilter] = useState<FilterState>(defaultFilter());
  const [studentList, setStudentList] = useState<StudentIndexItem[]>([]);
  const [activeDetail, setActiveDetail] = useState<StudentBlock | null>(null);
  const [current, setCurrent] = useState(0);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<null | "csv" | "pdf">(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  const [options, setOptions] = useState<Options>({
    grades: [], subjects: [], staff: [], students: [], locations: [],
    study_modes: [], assignment_statuses: [],
  });

  const indexReqRef = useRef(0);
  const detailReqRef = useRef(0);
  const params = useMemo(() => toParams(filter), [filter]);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let alive = true;
    reportsApi.studentTimetableOptions()
      .then((res) => { if (alive) setOptions(res as Options); })
      .catch((e: any) => { if (alive) setError(e.message || String(e)); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    const token = ++indexReqRef.current;
    setLoading(true); setError(null);
    try {
      const res = await reportsApi.studentTimetableIndex(params);
      if (token !== indexReqRef.current) return;
      setStudentList(res.data);
      setSummary(res.summary as Record<string, number>);
      setRange({ from: res.range.from, to: res.range.to });
      setCurrent(0);
      setActiveDetail(null);
      setRefreshedAt(new Date());
    } catch (e: any) {
      if (token === indexReqRef.current) setError(e.message || String(e));
    } finally {
      if (token === indexReqRef.current) { setLoading(false); setFirstLoad(false); }
    }
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const activeIndexItem = studentList[current];
  const activeStudentId = activeIndexItem?.student_id ?? null;
  const activeKey = `${paramsKey}|${activeStudentId ?? "null"}|${current}`;

  useEffect(() => {
    if (!activeIndexItem) { setActiveDetail(null); return; }
    const token = ++detailReqRef.current;
    setDetailLoading(true);
    reportsApi.studentTimetablePreview({ ...params, student_id: activeStudentId ?? undefined })
      .then((res) => {
        if (token !== detailReqRef.current) return;
        const block = (res.students && res.students[0]) ? (res.students[0] as unknown as StudentBlock) : null;
        setActiveDetail(block);
      })
      .catch((e: any) => {
        if (token === detailReqRef.current) setError(e.message || String(e));
      })
      .finally(() => {
        if (token === detailReqRef.current) setDetailLoading(false);
      });
  }, [activeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const doExport = async (kind: "csv" | "pdf") => {
    setExporting(kind); setError(null);
    try {
      const fn = kind === "csv" ? reportsApi.studentTimetableCsv : reportsApi.studentTimetablePdf;
      await fn(params);
    } catch (e: any) {
      setError(`Export failed: ${e.message || e}`);
    } finally {
      setExporting(null);
    }
  };

  const total = studentList.length;
  const weekLabel = range ? formatWeekRange(range.from, range.to) : "";

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: PAGE_BG, minHeight: "100%" }} className="stur-report-root">
      <PremiumHeader
        refreshedAt={refreshedAt}
        loading={loading}
        exporting={exporting}
        onRefresh={load}
        onCsv={() => doExport("csv")}
        onPdf={() => doExport("pdf")}
      />

      {/* ═══ Summary statistics strip ═════════════════════════════════════ */}
      <Card
        variant="outlined"
        sx={{
          mb: 2, borderRadius: 3, borderColor: HAIRLINE, overflow: "hidden",
        }}
        className="stur-stats"
      >
        <Box
          sx={{
            display: "flex", flexWrap: "wrap",
            "& > *": {
              borderRight: { md: `1px solid ${HAIRLINE}` },
              borderBottom: { xs: `1px solid ${HAIRLINE}`, md: "none" },
            },
            "& > *:last-of-type": {
              borderRight: "none",
              borderBottom: "none",
            },
          }}
        >
          <StatCell label="Students"  value={summary.total_students ?? 0}   icon={<PeopleAltOutlinedIcon fontSize="small" />} />
          <StatCell label="Classes"   value={summary.total_classes ?? 0}    icon={<ClassOutlinedIcon fontSize="small" />} />
          <StatCell label="Staff"     value={summary.total_staff ?? 0}      icon={<BadgeOutlinedIcon fontSize="small" />} />
          <StatCell label="Hours"     value={summary.total_hours ?? 0}      icon={<AccessTimeOutlinedIcon fontSize="small" />} />
          <StatCell label="Online"    value={summary.online_classes ?? 0}   icon={<LaptopMacOutlinedIcon fontSize="small" />} accent="#0ea5e9" />
          <StatCell label="In-Person" value={summary.in_person_classes ?? 0} icon={<PlaceOutlinedIcon fontSize="small" />} accent="#8b5cf6" />
          <StatCell label="Hybrid"    value={summary.hybrid_classes ?? 0}   icon={<HubOutlinedIcon fontSize="small" />} accent="#f59e0b" />
          <StatCell label="Locations" value={summary.total_locations ?? 0}  icon={<DomainOutlinedIcon fontSize="small" />} />
        </Box>
        {weekLabel && (
          <Box
            sx={{
              px: 2, py: 1, borderTop: `1px solid ${HAIRLINE}`,
              bgcolor: "#fafbfc",
            }}
          >
            <Typography sx={{ fontSize: 12, color: MUTED }}>
              Week: <Box component="span" sx={{ fontWeight: 700, color: INK }}>{weekLabel}</Box>
            </Typography>
          </Box>
        )}
      </Card>

      <Box className="stur-filters">
        <FilterPanel
          value={filter}
          onChange={setFilter}
          onClear={() => setFilter(defaultFilter())}
          loading={loading}
          options={options}
        />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
          className="stur-hide-print"
        >
          <Box sx={{ fontWeight: 700, mb: 0.25 }}>Unable to load timetable</Box>
          {error}
        </Alert>
      )}

      {firstLoad && loading ? (
        <Card variant="outlined" sx={{ py: 8, borderRadius: 3, borderColor: HAIRLINE, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <StepUpSpinner size="large" label="Loading student timetables" />
        </Card>
      ) : total === 0 ? (
        <Card
          variant="outlined"
          sx={{
            py: 6, textAlign: "center", borderRadius: 3, borderColor: HAIRLINE,
            bgcolor: "#fff",
          }}
        >
          <EventBusyOutlinedIcon sx={{ fontSize: 44, color: alpha(NAVY, 0.35), mb: 1 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
            No students match the current filters
          </Typography>
          <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75, mx: "auto", maxWidth: 420 }}>
            Try changing the week, clearing the student search, or removing location/grade filters.
          </Typography>
          <Button
            variant="outlined"
            sx={{ mt: 2, textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            onClick={() => setFilter(defaultFilter())}
          >
            Clear Filters
          </Button>
        </Card>
      ) : (
        <>
          {/* ═══ Student navigator ═════════════════════════════════════════ */}
          <Card
            variant="outlined"
            sx={{
              px: { xs: 1.5, md: 2 }, py: 1.25, mb: 2, borderRadius: 3, borderColor: HAIRLINE,
              display: "grid",
              gridTemplateColumns: { xs: "auto 1fr auto", md: "1fr auto 1fr" },
              alignItems: "center", columnGap: 1.5, rowGap: 1,
            }}
            className="stur-navigator stur-hide-print"
          >
            {/* Previous */}
            <Box sx={{ justifySelf: "start" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={() => setCurrent((c) => Math.max(0, c - 1))}
                disabled={current <= 0}
                sx={{
                  textTransform: "none", borderRadius: 2, fontWeight: 600,
                  borderColor: HAIRLINE, color: INK,
                  "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
                }}
              >
                Previous
              </Button>
            </Box>

            {/* Centered student identity */}
            <Box sx={{ textAlign: "center", minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14.5, fontWeight: 800, color: INK, lineHeight: 1.2,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
              >
                {activeIndexItem?.student_name ?? "—"}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: MUTED, mt: 0.25 }}>
                Student {current + 1} of {total}
              </Typography>
            </Box>

            {/* Next */}
            <Box sx={{ justifySelf: "end" }}>
              <Button
                size="small"
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                onClick={() => setCurrent((c) => Math.min(total - 1, c + 1))}
                disabled={current >= total - 1}
                sx={{
                  textTransform: "none", borderRadius: 2, fontWeight: 600,
                  borderColor: HAIRLINE, color: INK,
                  "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.04) },
                }}
              >
                Next
              </Button>
            </Box>
          </Card>

          {detailLoading && !activeDetail ? (
            <Card variant="outlined" sx={{ py: 8, borderRadius: 3, borderColor: HAIRLINE, display: "flex", justifyContent: "center" }}>
              <StepUpSpinner size="large" label={`Loading ${activeIndexItem?.student_name ?? "student"}`} />
            </Card>
          ) : activeDetail ? (
            <Box className="stur-timetable"><TimetableGrid student={activeDetail} /></Box>
          ) : null}
        </>
      )}

      {/* ── Print styles — hide chrome, flatten card shadows, keep timetable + review ── */}
      <style>{`
        @media print {
          @page { margin: 12mm; }
          html, body { background: #fff !important; }
          /* App chrome the report doesn't own — best-effort hide. Sidebar/topbar
             typically render into siblings of #root; if a repo has explicit
             classes for those, add them here. */
          .MuiDrawer-root, .MuiAppBar-root, aside, header[role="banner"] { display: none !important; }
          /* Report-owned controls we hide */
          .stur-hide-print, .stur-filters, .stur-navigator { display: none !important; }
          /* Flatten cards */
          .stur-report-root, .stur-report-root * {
            box-shadow: none !important;
          }
          .stur-report-root .MuiCard-root {
            border-color: #cbd5e1 !important;
          }
          /* Day-block page-break hinting */
          .stur-day-block { break-inside: avoid; page-break-inside: avoid; }
          .stur-class-card { break-inside: avoid; page-break-inside: avoid; }
          .stur-review { break-inside: avoid; page-break-inside: avoid; }
          /* Ensure link colours print as ink (they'll render as underlined text in most browsers anyway) */
          a { color: #0f172a !important; text-decoration: none !important; }
        }
      `}</style>
    </Box>
  );
}
